import { buildRequestSettings as buildRequest } from 'functions/buildRequestSettings';
import DataWrapper from 'interfaces/DataWrapper';
import Identifiable from 'interfaces/Identifiable';
import { GetErrorCause } from 'models/ErrorResponse';
import { HttpMethod } from 'models/HttpMethod';

import DataSetOperators from '../interfaces/DataSetOperators';


export interface QueryParam {
  name: string;
  value: any;
}

class ApiService<T> {
  private accessToken?: string;

  constructor(public baseUri: string, public entityName: string) { }

  public setAccessToken(newToken: string) {
    if (newToken) {
      this.accessToken = newToken;
    }
  }

  public async find(id: number): Promise<T> {
    const fetchResponse = await fetch(
      `${this.baseUri}/api/${this.entityName}/${id}`,
      this.buildRequestSettings(HttpMethod.GET),
    );
    return this.handleResponse<T>(fetchResponse);
  }

  public async all(
    operators: DataSetOperators,
    uriActionPath?: string,
  ): Promise<Array<T> | DataWrapper<T>> {
    const queryParams: QueryParam[] = Object.keys(operators)
      .map((key): QueryParam | undefined => {
        if ((operators as any)[key]) {
          return { name: key, value: (operators as any)[key] };
        }
        return undefined;
      })
      .filter((e) => e !== undefined) as QueryParam[];

    const uri = uriActionPath
      ? `${this.baseUri}/api/${this.entityName}/${uriActionPath}`
      : `${this.baseUri}/api/${this.entityName}`;

    const fetchResponse = await fetch(
      `${uri}?${this.buildQueryParams(queryParams)}`,
      this.buildRequestSettings(HttpMethod.GET),
    );
    return this.handleResponse<Array<T>>(fetchResponse);
  }

  public async create(entry: T, queryParams: URLSearchParams | undefined = undefined): Promise<T> {
    const paramString = queryParams ? `?${queryParams.toString()}` : "";
    const fetchResponse = await fetch(
      `${this.baseUri}/api/${this.entityName}${paramString}`,
      this.buildRequestSettings(HttpMethod.POST, entry),
    );
    return this.handleResponse<T>(fetchResponse);
  }

  public async execute(uriActionPath: string, method: HttpMethod, data?: any): Promise<Response> {
    const fetchResponse = await fetch(
      `${this.baseUri}/api/${this.entityName}/${uriActionPath}`,
      this.buildRequestSettings(method, data),
    );
    return fetchResponse;
  }

  // eslint-disable-next-line @typescript-eslint/no-shadow
  public async update<T extends Identifiable>(entry: T, queryParams: URLSearchParams | undefined = undefined): Promise<boolean> {
    const paramString = queryParams ? `?${queryParams.toString()}` : "";
    const fetchResponse = await fetch(
      `${this.baseUri}/api/${this.entityName}/${entry.id}${paramString}`,
      this.buildRequestSettings(HttpMethod.PUT, entry),
    );
    if (fetchResponse.status === 204) {
      return true;
    }
    throw new Error(`Error: ${fetchResponse.status}: ${fetchResponse.statusText}`);
  }

  public async delete(id: number): Promise<boolean> {
    const fetchResponse = await fetch(
      `${this.baseUri}/api/${this.entityName}/${id}`,
      this.buildRequestSettings(HttpMethod.DELETE),
    );
    if (fetchResponse.status === 204) {
      return true;
    }
    throw new Error(`Error: ${fetchResponse.status}: ${fetchResponse.statusText}`);
  }

  // eslint-disable-next-line class-methods-use-this
  private buildQueryParams(queryParams: QueryParam[]): string {
    return queryParams
      .map((qp) => (qp.value ? `${qp.name}=${encodeURIComponent(qp.value)}` : undefined))
      .join('&');
  }

  private buildRequestSettings(method: HttpMethod, bodyData?: any): RequestInit {
    return buildRequest(method, this.accessToken, bodyData);
  }

  // eslint-disable-next-line class-methods-use-this
  private async handleResponse<T2>(response: Response): Promise<T2> {
    if (response.ok) {
      const responseJson: T2 = await response.json();
      return responseJson;
    }
    throw new Error(`Error: ${response.status}: ${response.statusText}`, { cause: GetErrorCause(response) });
  }
}

export { ApiService };
